import { useState } from "react";
import { Copy, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { useAgents } from "@/shared/api/agents";
import AvatarCustomizer from "@/shared/avatar/AvatarCustomizer";
import { ListCard } from "@/shared/components/ListCard";
import {
  AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger, AlertDialogCancel,
} from "@/shared/components/ui/alert-dialog";
import { Pane, Row, TextInput, confirmDangerCls, confirmDialogCls } from "@/shared/components/settings-atoms";
import { BrandButton, GhostButton } from "@/shared/components/ui-kit";
import { Switch } from "@/shared/components/ui/switch";
import { Visibility } from "./Visibility";

import type { Agent } from "@/shared/types";

/* How the agent looks and is called, who can find it, how it publishes, and duplicating or
   deleting it — all of it applies at once, none of it is a draft. */
export default function GeneralSection({ agent, occupiedAvatars, onUpdateAgent, onDuplicateAgent, onDeleteAgent }: {
  agent: Agent;
  occupiedAvatars: readonly { seed: string; hue: number }[];
  onUpdateAgent: (updater: (a: Agent) => Agent) => void;
  onDuplicateAgent: () => void;
  onDeleteAgent: () => void;
}) {
  const [name, setName] = useState(agent.name);
  const [about, setAbout] = useState(agent.about ?? "");
  const [confirmText, setConfirmText] = useState("");
  // Visibility edits the API row, as Policies reads it.
  const row = useAgents().data?.find((found) => found.id === agent.id);
  const rename = () => {
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error("Give your agent a name");
      return;
    }
    setName(trimmed);
    onUpdateAgent((a) => ({ ...a, name: trimmed }));
  };

  return (
    <Pane title="General" desc="How this agent appears, who can find it and how it publishes, and what to do with it. Changes here apply at once.">
      <div className="mt-6 space-y-6">
        <ListCard label="Identity">
          <Row className="px-4" title="Icon" desc="Its own ink buddy. It moves while the agent works and turns amber while it has unpublished changes.">
            <AvatarCustomizer
              occupiedAvatars={occupiedAvatars}
              seed={agent.seed}
              hue={agent.hue}
              avatar={agent.avatar || null}
              onChange={(look) => onUpdateAgent((a) => ({ ...a, ...look }))}
            />
          </Row>
          <Row className="px-4" title="Name" desc="Shown in the sidebar, in chats and on every channel.">
            <div className="flex items-center gap-2 w-[320px] max-w-full">
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && rename()}
                placeholder="e.g. Research Analyst"
                aria-label="Name"
                className="flex-1 min-w-0 h-8 px-2.5 rounded-[8px] bg-[var(--surface)] ring-1 ring-[var(--hairline-strong)] focus:ring-[var(--hairline-focus)] outline-none text-[13px] text-[var(--text-primary)] placeholder:text-[var(--text-quaternary)] tracking-[-0.005em]"
              />
              <BrandButton onClick={rename} disabled={name === agent.name} className="disabled:opacity-40 disabled:pointer-events-none">Rename</BrandButton>
            </div>
          </Row>
          <Row className="px-4" title="About" desc="What it does, in a sentence or two. Its customers read it on its client app.">
            <div className="flex flex-col items-end gap-2 w-[320px] max-w-full">
              <textarea
                value={about}
                onChange={(e) => setAbout(e.target.value.slice(0, 500))}
                placeholder="e.g. Reviews your pull requests and leaves comments on GitHub."
                aria-label="About"
                rows={3}
                className="w-full resize-none px-2.5 py-2 rounded-[8px] bg-[var(--surface)] ring-1 ring-[var(--hairline-strong)] focus:ring-[var(--hairline-focus)] outline-none text-[13px] text-[var(--text-primary)] placeholder:text-[var(--text-quaternary)] tracking-[-0.005em]"
              />
              <BrandButton
                onClick={() => onUpdateAgent((a) => ({ ...a, about: about.trim() }))}
                disabled={about.trim() === (agent.about ?? "")}
                className="disabled:opacity-40 disabled:pointer-events-none"
              >
                Save
              </BrandButton>
            </div>
          </Row>
        </ListCard>

        {row && <Visibility agent={row} />}

        <ListCard label="Publishing">
          <Row className="px-4" title="Run evals first" desc="Publish runs this agent's eval cases on the draft first and shows what changed.">
            <Switch
              checked={agent.evalsBeforePublish ?? true}
              onCheckedChange={(on) => onUpdateAgent((a) => ({ ...a, evalsBeforePublish: on }))}
              aria-label="Run evals before publishing"
            />
          </Row>
        </ListCard>

        <ListCard label="Danger zone" danger>
          <Row className="px-4" title="Duplicate agent" desc="A fresh copy with the same instructions, model and apps — chats stay behind.">
            <GhostButton onClick={onDuplicateAgent}>
              <Copy className="w-3.5 h-3.5" strokeWidth={1.75} /> Duplicate
            </GhostButton>
          </Row>
          <Row className="px-4" title="Delete agent" desc="Permanently removes this agent with all its chats, tasks and knowledge.">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <GhostButton className="!text-[var(--danger)] !ring-[var(--danger-ring)] hover:!bg-[var(--danger-bg)]">
                  <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75} /> Delete agent
                </GhostButton>
              </AlertDialogTrigger>
              <AlertDialogContent className={`${confirmDialogCls} max-w-[420px]`}>
                <AlertDialogHeader>
                  <AlertDialogTitle className="text-[15px] tracking-[-0.01em]">Delete {agent.name}?</AlertDialogTitle>
                  <AlertDialogDescription className="text-[13px] leading-[1.6]">
                    All of its chats, tasks and knowledge are erased immediately. Type{" "}
                    <span className="font-medium text-[var(--text-primary)]">{agent.name}</span> to confirm.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <TextInput value={confirmText} onChange={setConfirmText} placeholder={agent.name} />
                <AlertDialogFooter>
                  <AlertDialogCancel className="rounded-full text-[13px]" onClick={() => setConfirmText("")}>
                    Cancel
                  </AlertDialogCancel>
                  <button
                    disabled={confirmText !== agent.name}
                    onClick={onDeleteAgent}
                    className={confirmDangerCls}
                  >
                    Delete forever
                  </button>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </Row>
        </ListCard>
      </div>
    </Pane>
  );
}
